'use client';

import Link from 'next/link';
import { cn } from '@/lib/utils';

interface ButtonProps {
  href?: string;
  children: React.ReactNode;
  variant?: 'primary' | 'outline' | 'ghost' | 'link';
  size?: 'sm' | 'md' | 'lg';
  showArrow?: boolean;
  hideArrowInitially?: boolean;
  className?: string;
  ariaLabel?: string;
  title?: string;
  rel?: string;
  target?: string;
}

export function Button({
  href = '#',
  children,
  variant = 'outline',
  size = 'md',
  showArrow = true,
  hideArrowInitially = false,
  className,
  ariaLabel,
  title,
  rel,
  target,
}: ButtonProps) {
  // Determine if link is external
  const isExternal = href?.startsWith?.('http') || href?.startsWith?.('//');
  const linkRel = rel || (isExternal ? 'noopener noreferrer' : undefined);
  const linkTarget = target || (isExternal ? '_blank' : undefined);

  // Generate aria-label from children if not provided
  const ariaLabelValue = ariaLabel || (typeof children === 'string' ? children : undefined);
  const baseStyles =
    'group/button relative inline-flex items-center gap-2 font-medium rounded-lg transition-all duration-300';

  const variants = {
    primary:
      'border-2 border-primary-600 dark:border-primary-500 bg-transparent text-primary-600 dark:text-primary-400 hover:bg-primary-700 dark:hover:bg-primary-700 hover:text-white dark:hover:text-white hover:border-primary-700 dark:hover:border-primary-700 hover:shadow-lg hover:shadow-primary-500/30',
    outline:
      'border-2 border-primary-500 dark:border-primary-400 bg-transparent text-primary-600 dark:text-primary-400 hover:bg-primary-700 dark:hover:bg-primary-700 hover:text-white dark:hover:text-white hover:border-primary-700 dark:hover:border-primary-700 hover:shadow-lg hover:shadow-primary-500/30 hover:-translate-y-0.5',
    ghost:
      'text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:text-primary-700 dark:hover:text-primary-300',
    link:
      'text-primary-600 dark:text-primary-400 hover:gap-2 px-0 text-sm font-medium',
  };

  const sizes = {
    sm: 'px-6 py-2.5 text-sm',
    md: 'px-8 py-3.5 text-base',
    lg: 'px-10 py-4 text-lg',
  };

  const isLinkVariant = variant === 'link';
  const gapClass = isLinkVariant ? 'gap-1' : 'gap-2';
  const sizeClass = isLinkVariant ? '' : sizes[size];

  return (
    <Link
      href={href || '#'}
      className={cn(
        baseStyles,
        variants[variant],
        !isLinkVariant && sizeClass,
        gapClass,
        className
      )}
      aria-label={ariaLabelValue}
      title={title || (typeof children === 'string' ? children : undefined)}
      rel={linkRel}
      target={linkTarget}
    >
      <span>{children}</span>
      {showArrow && (
        <span
          aria-hidden="true"
          className={cn(
            'transition-all duration-300',
            isLinkVariant || hideArrowInitially
              ? 'opacity-0 group-hover/button:opacity-100 translate-x-0 group-hover/button:translate-x-0.5'
              : 'group-hover/button:translate-x-1'
          )}
        >
          {isLinkVariant ? (
            '→'
          ) : (
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          )}
        </span>
      )}
    </Link>
  );
}

